import React, { useCallback } from 'react';

import { useGetFaces } from '@vlabs/api/artemy/client';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { parseISOToLocale } from '@vlabs/shared/utils/date';
import { Control } from '@vlabs/uikit';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import st from './SmallFaceCard.module.sass';
import { buildTestid } from '../../../lib/utils';
import type { BaseFaceCardPoint3Props } from '../base-face-card/BaseFaceCard.point3';
import { BaseFaceCardPoint3 } from '../base-face-card/BaseFaceCard.point3';

export interface SmallFaceCardPoint3Props extends Omit<BaseFaceCardPoint3Props, 'size' | 'src'> {
  selectable?: boolean;
  isSelected?: boolean;
  onSelect?: (next: boolean, externalId?: string) => void;
  rightButton?: React.JSX.Element;
}

const displayName = 'SmallFaceCardPoint3';
const testid = buildTestid(displayName);

export function SmallFaceCardPoint3({
  faceId,
  selectable = undefined,
  isSelected = undefined,
  onSelect = undefined,
  rightButton = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: SmallFaceCardPoint3Props): React.JSX.Element {
  const { t } = useTranslation();
  const getFace = useGetFaces({ face_ids: [faceId] }, {
    query: {
      enabled: !!faceId,
    },
  });
  const face = getFace.data?.faces?.[0];

  const handleCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onSelect?.(e.target.checked, face?.face_id);
  }, [onSelect, face?.face_id]);

  return (
    <div
      className={cn(
        st.Root,
        { [(st.Root_Selected as string)]: isSelected },
      )}
      {...testid(faceId, dataTestId)}
    >
      <BaseFaceCardPoint3
        faceId={face?.face_id}
        size="small"
        src={face?.avatar ? `/api/${face.avatar}` : undefined}
        {...props}
      />
      {!selectable && (
        <div
          className={st.CraeteTimeData}
          {...testid(faceId, 'CreateTime', dataTestId)}
        >
          <span
            className={st.CreateTimeLabel}
            {...testid(faceId, 'CreateTime', 'Label', dataTestId)}
          >
            {t('monokit:face card.face.created')}
          </span>
          <span
            className={st.CreateTimeValue}
            {...testid(faceId, 'CreateTime', 'Value', dataTestId)}
          >
            {parseISOToLocale(face?.create_time)}
          </span>
        </div>
      )}
      {!!selectable && !!face?.face_id && (
        <div
          className={st.SelectableData}
          {...testid(faceId, 'Selectable', dataTestId)}
        >
          <Control.Checkbox
            checked={isSelected}
            className={st.SmallFaceCardCheckbox}
            {...testid(faceId, 'Selectable', 'Checkbox', dataTestId)}
            onChange={handleCheckboxChange}
          />
          <span
            className={st.FaceIdLabel}
            {...testid(faceId, 'Selectable', 'FaceId', dataTestId)}
          >
            <TruncatedText
              hasTruncation
              lineClamp={2}
              tooltipContent={face?.face_id ?? ''}
            >
              {face?.face_id ?? ''}
            </TruncatedText>
          </span>
          {!!rightButton && (
            <div
              className={st.RightButton}
              {...testid(faceId, 'RightButton', dataTestId)}
            >
              {rightButton}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
